import { useState, useCallback, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import { EventContentArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AddActivityTimeSlots } from '@/components/business/features/calendar/components/AddActivityTimeSlots';
import { EditSlotModal } from '@/components/business/features/calendar/components/EditSlotModal';
import { DeleteSlotModal } from '@/components/business/features/calendar/components/DeleteSlotModal';
import { SuccessToast } from '@/components/business/features/calendar/components/SuccessToast';
import { BulkSlotManagement } from '@/components/business/features/calendar/components/BulkSlotManagement';
import { useEnhancedCalendarEvents } from '@/hooks/useEnhancedCalendarEvents';
import { Alert, AlertDescription } from '@/components/ui/alert';
import CalendarStyles from "@/components/business/features/calendar/components/CalendarStyles";
import { EnhancedCalendarEventContent } from "@/components/business/features/calendar/components/EnhancedCalendarEventContent";
import { createBulkSlots, updateBulkSlots, deleteSlot } from '@/utils/businessAppApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon, 
  Calendar,
  Sparkles,
  CheckCircle,
  Clock,
  Users,
  Globe,
  Activity,
  Target,
  BarChart3,
  Settings,
  Edit,
  Trash2,
  AlertTriangle
} from 'lucide-react';

export function ActivityCalendar() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('business', { keyPrefix: 'calendar' });
  const { t: tc } = useTranslation('common');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'timeGridWeek' | 'dayGridMonth'>('dayGridMonth');
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<any>(null);
  const [deletingSlot, setDeletingSlot] = useState<any>(null);
  const [isSlotOperationLoading, setIsSlotOperationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBulkManagementOpen, setIsBulkManagementOpen] = useState(false);
  const [successToast, setSuccessToast] = useState({ isVisible: false, message: '' });
  
  const calendarRef = useRef<FullCalendar>(null);
  const isMounted = useRef(true);
  const lastDateRef = useRef<string>('');
  const dateUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: events, isLoading: eventsLoading } = useEnhancedCalendarEvents(currentDate, currentView);

  // Handle creating activity slots
  const handleCreateActivitySlots = async (formData: any) => {
    setIsSlotOperationLoading(true);
    try {
      await createBulkSlots(formData);
      queryClient.invalidateQueries({ queryKey: ['enhanced-calendar-events'] as any });
      setIsAddingActivity(false);
      setSuccessToast({ isVisible: true, message: t('activitySlotsCreated') });
    } catch (error) {
      console.error('Error creating slots:', error);
      toast({
        title: tc('error'),
        description: t('failedToCreateSlots'),
        variant: "destructive",
      });
    } finally {
      setIsSlotOperationLoading(false);
    }
  };

  // Handle individual slot operations
  const handleEditSlot = (slotId: string, slotData: any) => {
    console.log('Editing slot:', slotId, slotData);
    setEditingSlot({ id: slotId, ...slotData });
    setIsEditModalOpen(true);
  };

  const handleDeleteSlot = (slotId: string, slotData: any) => {
    console.log('🗑️ Preparing to delete slot:', slotId, slotData);
    setDeletingSlot({ id: slotId, ...slotData });
    setIsDeleteModalOpen(true);
  };

  // Handle custom adventure booking view
  const handleViewCustomAdventure = (bookingId: string, bookingData: any) => {
    console.log('📅 Viewing custom adventure booking:', bookingId, bookingData);
    // For now, show a toast with booking info - could be enhanced with a modal later
    toast({
      title: "Custom Adventure Booking",
      description: `${bookingData.customerName} - ${bookingData.requestNumber}`,
      duration: 3000,
    });
  };

  // Handle confirming slot deletion
  const handleConfirmDeleteSlot = async (slotId: string) => {
    setIsSlotOperationLoading(true);
    try {
      console.log('🗑️ Deleting slot:', slotId);
      await deleteSlot(slotId);
      queryClient.invalidateQueries({ queryKey: ['enhanced-calendar-events'] as any });
      setIsDeleteModalOpen(false);
      setDeletingSlot(null);
      setSuccessToast({ isVisible: true, message: t('activitySlotDeleted') });
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('❌ Error deleting slot:', error);
      
      // Handle error with toast in component
      toast({
        title: tc('error'),
        description: t('failedToDeleteSlot'),
        variant: "destructive",
      });
      
      setError(t('failedToDeleteSlot'));
    } finally {
      setIsSlotOperationLoading(false);
    }
  };

  // Handle saving edited slot
  const handleSaveEditedSlot = async (slotData: any) => {
    setIsSlotOperationLoading(true);
    try {
      // Transform the modal form data to match the API expected format
      const apiPayload = {
        slots: [{
          _id: slotData.id, // Use _id instead of id to match backend expectations
          startTime: slotData.startTime,
          endTime: slotData.endTime,
          capacity: parseInt(slotData.capacity) || 10,
          price: parseFloat(slotData.price) || 0,
          status: slotData.status || 'available'
        }]
      };

      console.log('🔄 Updating slot with payload:', apiPayload);
      
      await updateBulkSlots(apiPayload);
      queryClient.invalidateQueries({ queryKey: ['enhanced-calendar-events'] as any });
      
      // Success feedback
      console.log('✅ Slot updated successfully:', slotData.id);
      setError(null); // Clear any previous errors
      setSuccessToast({ isVisible: true, message: t('activitySlotUpdated') });
      
    } catch (error) {
      console.error('❌ Error updating slot:', error);
      setError(t('failedToUpdateSlot'));
      throw error; // Re-throw to prevent modal from closing
    } finally {
      setIsSlotOperationLoading(false);
    }
  };

  // Handle bulk operations
  const handleBulkEdit = async (slots: any[], updates: any) => {
    setIsSlotOperationLoading(true);
    try {
      // Transform slots data to match backend expectations
      const transformedSlots = slots.map(slot => ({
        _id: slot.id, // Use _id instead of id for backend
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: slot.capacity,
        price: slot.price,
        status: slot.status,
        booked: slot.booked || 0 // Include booked count for backend calculations
      }));

      console.log('🔄 Bulk updating slots:', {
        originalCount: slots.length,
        transformedSlots: transformedSlots.slice(0, 2), // Log first 2 for debugging
        updates
      });

      await updateBulkSlots({ slots: transformedSlots });
      queryClient.invalidateQueries({ queryKey: ['enhanced-calendar-events'] as any });
      console.log('✅ Bulk edit completed:', slots.length, 'slots updated');
    } catch (error) {
      console.error('❌ Error in bulk edit:', error);
      setError(t('failedToUpdateSlots'));
      throw error;
    } finally {
      setIsSlotOperationLoading(false);
    }
  };

  const handleBulkDelete = async (slotIds: string[]) => {
    setIsSlotOperationLoading(true);
    try {
      // Delete slots one by one (API doesn't support bulk delete)
      await Promise.all(slotIds.map(id => deleteSlot(id)));
      queryClient.invalidateQueries({ queryKey: ['enhanced-calendar-events'] as any });
      console.log('Bulk delete completed:', slotIds.length, 'slots deleted');
    } catch (error) {
      console.error('Error in bulk delete:', error);
      setError(t('failedToDeleteSlots'));
      throw error;
    } finally {
      setIsSlotOperationLoading(false);
    }
  };

  // Convert events to slot summaries for bulk management
  const getSlotSummaries = () => {
    if (!events || !Array.isArray(events)) return [];
    
    return events.map(event => ({
      id: event.id || '',
      title: event.title,
      date: event.start ? new Date(event.start).toISOString().split('T')[0] : '',
      startTime: event.start ? new Date(event.start).toTimeString().slice(0, 5) : '',
      endTime: event.end ? new Date(event.end).toTimeString().slice(0, 5) : '',
      capacity: event.extendedProps?.capacity || 0,
      booked: event.extendedProps?.booked || 0,
      price: event.extendedProps?.price || 0
    }));
  };

  // Guard for event content rendering
  const renderEventContent = useCallback((eventInfo: EventContentArg) => {
    if (!isMounted.current) return null;

    try {
      return (
        <EnhancedCalendarEventContent 
          eventInfo={eventInfo} 
          onEditSlot={handleEditSlot}
          onDeleteSlot={handleDeleteSlot}
          onViewCustomAdventure={handleViewCustomAdventure}
        />
      );
    } catch (error) {
      console.error('Error rendering event:', error);
      return <div className="p-2 text-xs text-red-500">Error</div>;
    }
  }, [handleEditSlot, handleDeleteSlot, handleViewCustomAdventure]);

  // Track mounting state and initialize date ref
  useEffect(() => {
    isMounted.current = true;
    // Initialize the date ref with current date
    lastDateRef.current = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
    
    return () => {
      isMounted.current = false;
      // Clean up any pending timeout
      if (dateUpdateTimeoutRef.current) {
        clearTimeout(dateUpdateTimeoutRef.current);
      }
    };
  }, []); // Only run once on mount

  // Handle calendar cleanup safely
  useEffect(() => {
    return () => {
      // Cleanup function for calendar component
    };
  }, []);
  
  // Handle view changes without causing infinite re-renders
  const handleViewDidMount = useCallback((viewInfo: any) => {
    if (!isMounted.current) return;

    // Only update view type if it's actually different, but don't remount
    if (viewInfo.view.type !== currentView) {
      console.log('📅 [Calendar] View changed:', {
        from: currentView,
        to: viewInfo.view.type
      });
      setCurrentView(viewInfo.view.type);
      // Remove the calendar remount - this was causing the navigation issues
    }
  }, [currentView]);

  // Handle date changes - let FullCalendar manage navigation
  const handleDatesSet = useCallback((dateInfo: any) => {
    if (!isMounted.current) return;
    
    // Use a debounced approach to prevent rapid state updates
    const newDate = new Date(dateInfo.start);
    const newDateKey = `${newDate.getFullYear()}-${newDate.getMonth()}`;
    
    // Only update if this is a genuinely different month/year and not a rapid change
    if (lastDateRef.current !== newDateKey) {
      console.log('🔄 [NAVIGATION] Date range updated:', {
        from: lastDateRef.current,
        to: newDateKey,
        newDate: newDate.toDateString(),
        dateRange: {
          start: dateInfo.start.toISOString().split('T')[0],
          end: dateInfo.end.toISOString().split('T')[0]
        }
      });
      
      lastDateRef.current = newDateKey;
      
      // Debounce the state update to prevent rapid changes
      if (dateUpdateTimeoutRef.current) {
        clearTimeout(dateUpdateTimeoutRef.current);
      }
      dateUpdateTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          console.log('🔄 [STATE UPDATE] Setting currentDate to:', newDate.toDateString());
          setCurrentDate(newDate);
        }
      }, 100); // 100ms debounce
    }
  }, []); // No dependencies to prevent recreation

  // Show error alert if needed
  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive" className="border-red-200/80 bg-red-50/80 shadow-md">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="flex items-center justify-between text-red-800">
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setError(null)}
              className="ml-4 border-red-200/80 text-red-700 hover:bg-red-50 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              {t('dismiss')}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Calculate calendar statistics
  const totalEvents = (events && Array.isArray(events)) ? events.length : 0;
  const todayEvents = (events && Array.isArray(events)) ? events.filter(event => {
    const eventDate = new Date(event.start || '');
    const today = new Date();
    return eventDate.toDateString() === today.toDateString();
  }).length : 0;

  return (
    <div className="space-y-8">
      <CalendarStyles />
      
      {/* Modern Header Section - Enhanced with Business Design System */}
      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-emerald-500 to-purple-500 flex items-center justify-center shadow-md">
                  <Sparkles className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent">
                  {t('activityCalendar')}
                </h1>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  {t('calendarSubtitle')}
                </p>
              </div>
            </div>
            
            {/* Status Pills - Enhanced with Better Contrast */}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm font-medium text-emerald-700">{t('calendarActive')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm">
                <Activity className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">{totalEvents} {t('totalEvents')}</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200/80 shadow-sm">
                <Target className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">{todayEvents} {t('today')}</span>
              </div>
            </div>
          </div>
          
          {/* Quick Actions - Enhanced Button Styling */}
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              className="border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('viewAnalytics')}
            </Button>
            <Button 
              onClick={() => setIsBulkManagementOpen(true)}
              variant="outline"
              className="border-purple-200/80 text-purple-700 hover:bg-purple-50 shadow-sm hover:shadow-md transition-shadow duration-200"
              disabled={!events || !Array.isArray(events) || events.length === 0}
            >
              <Settings className="w-4 h-4 mr-2" />
              {t('bulkManage')}
            </Button>
            <Button 
              onClick={() => setIsAddingActivity(true)}
              className="bg-emerald-600 hover:bg-emerald-700 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              {t('createActivitySlot')}
            </Button>
          </div>
        </div>
      </div>

      {/* Enhanced Calendar Card */}
      <Card className="border border-slate-200/60 shadow-lg shadow-slate-900/8 bg-white overflow-hidden hover:shadow-xl hover:shadow-slate-900/12 transition-shadow duration-300">
        <CardHeader className="bg-slate-50/80 border-b border-slate-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold text-slate-900">{t('scheduleOverview')}</CardTitle>
                <p className="text-sm text-slate-600">
                  {t('viewingSchedule', { 
                    view: currentView === 'timeGridWeek' ? 'week' : 'month', 
                    total: totalEvents 
                  })}
                </p>
              </div>
            </div>
            
            {/* Calendar Stats - Enhanced with Design System Colors */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">{totalEvents}</div>
                <div className="text-xs text-slate-600">{t('totalEvents')}</div>
              </div>
              <div className="w-px h-8 bg-slate-200/60" />
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{todayEvents}</div>
                <div className="text-xs text-slate-600">{t('today')}</div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 bg-white">
          {eventsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-slate-600">{t('loadingCalendarEvents')}</span>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm overflow-hidden">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={currentView}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={events || []}
                eventContent={renderEventContent}
                height="700px"
                nowIndicator
                selectable
                selectMirror
                dayMaxEvents={4}
                moreLinkContent={({ num }) => `+${num} more`}
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: false,
                  hour12: false
                }}
                datesSet={handleDatesSet}
                viewDidMount={handleViewDidMount}
                // loading={(state) => console.log("📅 [Calendar] Loading state:", state)}
                // navLinkDayClick={(date) => console.log("📅 [Calendar] Day clicked:", date)}
                // dateClick={(info) => console.log("📅 [Calendar] Date clicked:", info.dateStr)}
                firstDay={1} // Monday as first day of week
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddActivityTimeSlots
        isOpen={isAddingActivity}
        onClose={() => setIsAddingActivity(false)}
        onSubmit={handleCreateActivitySlots}
      />

      <EditSlotModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingSlot(null);
        }}
        onSave={handleSaveEditedSlot}
        slotData={editingSlot}
        isLoading={isSlotOperationLoading}
      />

      <DeleteSlotModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingSlot(null);
        }}
        onConfirm={() => handleConfirmDeleteSlot(deletingSlot?.id)}
        slotData={deletingSlot}
        isLoading={isSlotOperationLoading}
      />

      <BulkSlotManagement
        isOpen={isBulkManagementOpen}
        onClose={() => setIsBulkManagementOpen(false)}
        onBulkEdit={handleBulkEdit}
        onBulkDelete={handleBulkDelete}
        slots={getSlotSummaries()}
        isLoading={isSlotOperationLoading}
      />

      {/* Success Toast */}
      <SuccessToast
        isVisible={successToast.isVisible}
        message={successToast.message}
        onClose={() => setSuccessToast({ isVisible: false, message: '' })}
      />
    </div>
  );
}
